/*
 * Cart savings helper + the cart drawer's applied-coupon summary row.
 *
 * Savings are kept in two deliberately separate buckets:
 *   couponSavings    - money taken off by discount allocations Shopify reports
 *   priceDropSavings - compare-at vs selling price, only on lines with no coupon
 * A line contributes to exactly one bucket, never both: once a coupon is applied
 * to a line its allocation already prices in the compare-at difference, so adding
 * the price drop on top would double count it.
 *
 * All amounts are handled in paise (Shopify's cart JSON unit) and only rounded
 * to whole rupees at format time.
 */
(function () {
  'use strict';

  var rupees = new Intl.NumberFormat('en-IN');

  function formatMoney(paise) {
    return 'Rs.' + rupees.format(Math.round(paise / 100));
  }

  /*
   * lines: [{ productName, quantity, sellingPrice, compareAtPrice, discounts: [{ code, amount }] }]
   *   sellingPrice / compareAtPrice are per-unit paise; compareAtPrice may be null.
   *   discounts[].amount is the allocation for the whole line (Shopify reports line totals).
   */
  function computeSavings(lines) {
    var appliedCoupons = [];
    var couponBreakdown = [];
    var priceDropBreakdown = [];
    var couponSavings = 0;
    var priceDropSavings = 0;

    (lines || []).forEach(function (line) {
      var quantity = line.quantity || 0;
      var discounts = line.discounts || [];

      if (discounts.length > 0) {
        discounts.forEach(function (discount) {
          // Back out the unit amount so both buckets read as (perUnit * qty).
          var couponDiscount = quantity > 0 ? discount.amount / quantity : 0;
          var amount = couponDiscount * quantity;

          couponSavings += amount;
          couponBreakdown.push({
            code: discount.code,
            productName: line.productName,
            amount: amount
          });

          if (appliedCoupons.indexOf(discount.code) === -1) {
            appliedCoupons.push(discount.code);
          }
        });

        // Coupon lines stop here - see the double-counting note above.
        return;
      }

      var compareAtPrice = line.compareAtPrice;
      var sellingPrice = line.sellingPrice || 0;

      if (compareAtPrice != null && compareAtPrice > sellingPrice) {
        var drop = (compareAtPrice - sellingPrice) * quantity;
        priceDropSavings += drop;
        priceDropBreakdown.push({
          productName: line.productName,
          amount: drop
        });
      }
    });

    return {
      appliedCoupons: appliedCoupons,
      couponSavings: couponSavings,
      priceDropSavings: priceDropSavings,
      totalSavings: couponSavings + priceDropSavings,
      couponBreakdown: couponBreakdown,
      priceDropBreakdown: priceDropBreakdown
    };
  }

  window.CartSavings = {
    computeSavings: computeSavings,
    formatMoney: formatMoney
  };

  class CartSavingsSummary extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('[data-cart-savings-toggle]');
      this.panel = this.querySelector('[data-cart-savings-panel]');
      this.list = this.querySelector('[data-cart-savings-list]');

      if (!this.toggleButton || !this.panel) return;

      this.toggleButton.addEventListener('click', () => {
        this.setExpanded(this.getAttribute('data-expanded') !== 'true');
      });

      this.update();

      // Local UI state, collapsed by default. The drawer replaces this element
      // on every cart mutation, so totals are always recomputed, never cached.
      this.setExpanded(false);
    }

    readLines() {
      var payload = this.querySelector('[data-cart-savings-lines]');
      if (!payload) return [];
      try {
        return JSON.parse(payload.textContent);
      } catch (error) {
        return [];
      }
    }

    update() {
      var savings = computeSavings(this.readLines());
      var count = savings.appliedCoupons.length;
      var countLabel = this.querySelector('[data-cart-savings-count]');
      // Lives in the totals block, outside this element - look it up drawer-wide.
      var totalLabel = (this.closest('cart-drawer') || document).querySelector('[data-cart-savings-total]');
      var footerTotal = this.querySelector('[data-cart-savings-footer-total]');

      if (countLabel) {
        countLabel.textContent = count + (count === 1 ? ' coupon applied' : ' coupons applied');
      }
      if (totalLabel) {
        totalLabel.textContent = 'Total saved ' + formatMoney(savings.totalSavings);
      }
      if (footerTotal) {
        footerTotal.textContent = formatMoney(savings.totalSavings);
      }
      if (this.list) {
        this.renderBreakdown(savings);
      }
    }

    renderBreakdown(savings) {
      var rows = savings.couponBreakdown.map(function (entry) {
        return {
          modifier: 'coupon',
          label: entry.code + ' \u00b7 ' + entry.productName,
          amount: entry.amount
        };
      }).concat(savings.priceDropBreakdown.map(function (entry) {
        return {
          modifier: 'price-drop',
          label: 'Price drop \u00b7 ' + entry.productName,
          amount: entry.amount
        };
      }));

      this.list.textContent = '';

      rows.forEach(function (row) {
        var item = document.createElement('li');
        item.className = 'cart-savings__row cart-savings__row--' + row.modifier;

        var label = document.createElement('span');
        label.className = 'cart-savings__row-label';
        label.textContent = row.label;

        var amount = document.createElement('span');
        amount.className = 'cart-savings__row-amount';
        amount.textContent = '\u2212 ' + formatMoney(row.amount);

        item.appendChild(label);
        item.appendChild(amount);
        this.list.appendChild(item);
      }, this);
    }

    setExpanded(expanded) {
      this.setAttribute('data-expanded', expanded ? 'true' : 'false');
      this.toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.panel.style.setProperty('--cart-savings-panel-height', this.panel.scrollHeight + 'px');
    }
  }

  if (!customElements.get('cart-savings-summary')) {
    customElements.define('cart-savings-summary', CartSavingsSummary);
  }
})();
